import { ComponentProps, useContext, useMemo } from "react";
import TagManager from "react-gtm-module";
import { useRouter } from "next/router";
import { Interpolation, Theme, css } from "@emotion/react";
import { ContentGridMixContentQuery } from "components/ContentGridMixContentQuery";
import { ContentGridQuery } from "components/ContentGridQuery";
import { Empty } from "components/Empty";
import { ContentViewTypePicker } from "components/pickers/ContentViewTypePicker";
import { HorizontalPicker } from "components/pickers/HorizontalPicker";
import { SectionScaffold } from "components/scaffolds/SectionScaffold";
import { AppContext } from "contexts/AppContext";
import { theme } from "styles/theme";
import { zIndex } from "styles/zIndex";
import {
  MIX_CONTENT_TAG_ENUM_KOREAN,
  ORDERED_MIX_CONTENT_TAG_ENUM,
} from "utils/enum";

const LIMIT = 60;

export const RecentSection = ({
  contentSection,
  variables,
  ...props
}: Omit<
  ComponentProps<
    typeof ContentGridQuery<MixContentQuery, MixContentQueryVariables>
  >,
  "useQuery" | "extractor" | "updateQuery"
> & { css?: Interpolation<Theme> }) => {
  const router = useRouter();
  const { contentViewType } = useContext(AppContext);

  const tag = useMemo(
    () =>
      ORDERED_MIX_CONTENT_TAG_ENUM.find((tag) => tag === router.query.tag) ??
      null,
    [router],
  );

  return (
    <SectionScaffold {...props}>
      <SectionScaffold.Header>
        <SectionScaffold.Title>
          <span>최신 콘텐츠</span>
          {tag !== null && (
            <span
              css={css`
                margin-left: 12px;
                color: ${theme.color.grey_a};

                @media (max-width: 1024px) {
                  display: none;
                }
              `}
            >
              #{MIX_CONTENT_TAG_ENUM_KOREAN[tag]}
            </span>
          )}
        </SectionScaffold.Title>
        <SectionScaffold.Controls>
          <ContentViewTypePicker
            css={css`
              @media (max-width: 768px) {
                display: none;
              }
            `}
          />
        </SectionScaffold.Controls>
      </SectionScaffold.Header>
      <SectionScaffold.Contents>
        <HorizontalPicker<Mix_Content_Tag_Enum_Enum | null>
          css={css`
            display: none;

            @media (max-width: 1024px) {
              position: sticky;
              top: var(--header-height);
              background: ${theme.color.background};
              display: block;
              z-index: ${zIndex.categoryPicker};
              margin-bottom: 28px;
              margin-top: 8px;
            }
          `}
          items={[
            { value: null, label: "전체" },
            ...ORDERED_MIX_CONTENT_TAG_ENUM.map((value) => ({
              value,
              label: MIX_CONTENT_TAG_ENUM_KOREAN[value],
            })),
          ]}
          value={tag}
          onChange={(value) => {
            if (value) {
              router.push(`?tag=${value}`);

              TagManager.dataLayer({
                dataLayer: {
                  event: "content_category_click",
                  value,
                },
              });
            } else {
              router.push("/");
            }
          }}
        />
        <ContentGridMixContentQuery
          contentSection={contentSection}
          contentViewType={contentViewType}
          limit={LIMIT}
          variables={variables}
          empty={
            <Empty
              imgurl="/content-empty.png"
              mainText="추천할 만한 콘텐츠를 열심히 찾고 있어요."
              subText="조금만 기다려 주세요!"
            />
          }
        />
      </SectionScaffold.Contents>
    </SectionScaffold>
  );
};
